/**
 * Helpers for Admin Patient Management: group_list pipe format, expiry date, search merge, PATCH payload.
 */

function toNumberIds (ids) {
  if (!Array.isArray(ids)) return []
  return ids
    .map((x) => Number(x))
    .filter((n) => !Number.isNaN(n))
}

export function parseGroupList (value) {
  if (Array.isArray(value)) return toNumberIds(value)
  if (!value || typeof value !== 'string') return []
  return toNumberIds(value.split('|').filter(Boolean))
}

export function serializeGroupList (ids) {
  const nums = toNumberIds(ids)
  if (!nums.length) return '|'
  return '|' + nums.map((id) => id + '|').join('')
}

export function ensurePrimaryInGroupList (ids, groupId) {
  const nums = parseGroupList(ids)
  const primary = Number(groupId)
  if (Number.isNaN(primary)) return nums
  if (!nums.includes(primary)) return [primary, ...nums]
  return nums
}

export function formatExpStampDate (value) {
  if (value == null) return null
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    const y = value.getFullYear()
    const mo = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${y}-${mo}-${day}`
  }
  const trimmed = String(value).trim()
  if (!trimmed) return null
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)
  if (match) return match[1]
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

export function mergePatientsByRid (resultLists) {
  const seen = new Set()
  const items = []
  ;(resultLists || []).forEach((list) => {
    ;(list || []).forEach((row) => {
      if (row == null || row.rid == null) return
      const key = String(row.rid)
      if (seen.has(key)) return
      seen.add(key)
      items.push(row)
    })
  })
  return items
}

export function paginateItems (items, page, itemsPerPage) {
  const list = Array.isArray(items) ? items : []
  const size = Number(itemsPerPage) > 0 ? Number(itemsPerPage) : 10
  const p = Number(page) > 0 ? Number(page) : 1
  const start = (p - 1) * size
  return list.slice(start, start + size)
}

export function resolveGroupDescribe (groupId, groupList, extraGroups) {
  if (groupId == null || groupId === '') return '-'
  const all = [...(groupList || []), ...(extraGroups || [])]
  const found = all.find((g) => Number(g.group_id) === Number(groupId))
  return (found && found.group_describe) ? found.group_describe : String(groupId)
}

export function policyStillAvailable (policyNo, insuranceItems) {
  if (policyNo == null || policyNo === '') return false
  return (insuranceItems || []).some(
    (item) => String(item.insurance_policy_no) === String(policyNo)
  )
}

export function normalizeInsuranceList (data) {
  if (Array.isArray(data)) return data.filter(Boolean)
  if (data && Array.isArray(data.items)) return data.items.filter(Boolean)
  if (data && Array.isArray(data.records)) return data.records.filter(Boolean)
  if (data && typeof data === 'object' && (data.insurance_policy_no != null || data.insurance_name)) {
    return [data]
  }
  return []
}

export function insuranceOptionText (item) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)
  return item.insurance_name || item.insurance_text || String(item.insurance_policy_no || '')
}

export const PATIENT_FIELD_LIMITS = {
  patient_id: 20,
  record_id: 20,
  hn_rama: 15,
  full_name: 200,
  full_name_eng: 200,
  sex: 1,
  patient_stage: 1,
  dob: 20,
  phone_mobile: 20,
  phone_office: 20,
  email_1: 80,
  department: 100,
  unit_subdept: 100,
  pos_name: 100,
  insurance_policy_no: 30,
  contact_person: 50,
  past_surg_note: 500,
  lineId: 255
}

const PATIENT_PATCH_TEXT_KEYS = [
  'hn_rama',
  'full_name',
  'full_name_eng',
  'sex',
  'patient_stage',
  'dob',
  'phone_mobile',
  'phone_office',
  'email_1',
  'department',
  'unit_subdept',
  'pos_name',
  'insurance_policy_no',
  'contact_person',
  'past_surg_note',
  'pat_img'
]

function boundedText (value, max) {
  const text = String(value).trim()
  if (!max || text.length <= max) return text
  return text.slice(0, max)
}

export function buildPatientAdminPatch (patient, form) {
  const src = form || {}
  const row = patient || {}
  const payload = {
    rid: src.rid != null && src.rid !== '' ? src.rid : row.rid,
    patient_id: boundedText(pickIdentity(src.patient_id, row.patient_id), PATIENT_FIELD_LIMITS.patient_id),
    record_id: boundedText(pickIdentity(src.record_id, row.record_id), PATIENT_FIELD_LIMITS.record_id),
    group_id: src.group_id
  }
  PATIENT_PATCH_TEXT_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(src, key)) return
    const value = src[key]
    if (value === undefined || value === null || String(value).trim() === '') return
    const max = PATIENT_FIELD_LIMITS[key]
    payload[key] = max ? boundedText(value, max) : String(value).trim()
  })
  if (Object.prototype.hasOwnProperty.call(src, 'pos_priority') && src.pos_priority !== '' && src.pos_priority != null) {
    const n = Number(src.pos_priority)
    if (Number.isInteger(n) && n >= 0 && n <= 255) payload.pos_priority = n
  }
  if (Object.prototype.hasOwnProperty.call(src, 'related_rid') && src.related_rid !== '' && src.related_rid != null) {
    const n = Number(src.related_rid)
    if (Number.isInteger(n)) payload.related_rid = n
  }
  if (Object.prototype.hasOwnProperty.call(src, 'group_list')) {
    const ids = ensurePrimaryInGroupList(src.group_list, payload.group_id)
    payload.group_list = serializeGroupList(ids)
  }
  // lineId: optional. Non-empty overwrites. Explicit clear sends "". Omit otherwise (server keeps).
  if (src.clearLineId === true) {
    payload.lineId = ''
  } else if (Object.prototype.hasOwnProperty.call(src, 'lineId')) {
    const value = src.lineId
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      payload.lineId = boundedText(value, PATIENT_FIELD_LIMITS.lineId)
    }
  }
  return payload
}

function pickIdentity (formValue, rowValue) {
  if (formValue != null && formValue !== '') return formValue
  return rowValue
}

export const ADMIN_PATIENT_SORT_FIELDS = [
  'rid',
  'patient_id',
  'record_id',
  'full_name',
  'age_year',
  'sex',
  'hn_rama'
]

export function buildAdminPatientQuery (param) {
  const src = param || {}
  const query = {}
  const keyword = src.keyword != null ? String(src.keyword).trim() : ''
  if (keyword) query.keyword = keyword
  if (src.rid != null && src.rid !== '') {
    const rid = Number(src.rid)
    if (!Number.isNaN(rid)) query.rid = rid
  }
  query.sortBy = ADMIN_PATIENT_SORT_FIELDS.indexOf(src.sortBy) >= 0 ? src.sortBy : 'rid'
  query.sortDesc = src.sortDesc === true || src.sortDesc === 'true' || src.sortDesc === 1
  const page = Number(src.page)
  query.page = page >= 1 ? page : 1
  const per = Number(src.itemsPerPage)
  query.itemsPerPage = per >= 1 ? per : 10
  return query
}

const ADMIN_PATIENT_POST_KEYS = [
  'patient_id',
  'record_id',
  'group_id',
  'hn_rama',
  'full_name',
  'full_name_eng',
  'sex',
  'dob',
  'phone_mobile',
  'phone_office',
  'email_1',
  'department',
  'unit_subdept',
  'pos_name',
  'insurance_policy_no',
  'contact_person',
  'pat_img',
  'patient_stage',
  'past_surg_note'
]

export function buildPatientAdminPost (form) {
  const src = form || {}
  const payload = {}
  ADMIN_PATIENT_POST_KEYS.forEach((key) => {
    const value = src[key]
    if (value === undefined || value === null || value === '') return
    const max = PATIENT_FIELD_LIMITS[key]
    payload[key] = max ? boundedText(value, max) : value
  })
  if (src.pos_priority !== '' && src.pos_priority != null) {
    const n = Number(src.pos_priority)
    if (Number.isInteger(n) && n >= 0 && n <= 255) payload.pos_priority = n
  }
  if (src.related_rid !== '' && src.related_rid != null) {
    const n = Number(src.related_rid)
    if (Number.isInteger(n)) payload.related_rid = n
  }
  return payload
}

export function patientMaxLengthRule (max) {
  return (value) => {
    if (value == null || value === '') return true
    return String(value).length <= max || `ไม่เกิน ${max} ตัวอักษร`
  }
}

export function firstAdminPatient (data) {
  if (!data) return null
  if (Array.isArray(data.items)) return data.items[0] || null
  if (data.rid != null) return data
  return null
}

export function uniqueGroupIds (groupList) {
  const seen = new Set()
  const ids = []
  ;(groupList || []).forEach((g) => {
    if (g == null || g.group_id == null || g.group_id === '') return
    const id = Number(g.group_id)
    if (Number.isNaN(id) || seen.has(id)) return
    seen.add(id)
    ids.push(id)
  })
  return ids
}

export function messageFromUpdatePatientError (err) {
  const data = err && err.response && err.response.data
  const details = data && data.details
  if (Array.isArray(details) && details.length) {
    const texts = details
      .map((item) => (item && item.message) || '')
      .filter(Boolean)
    if (texts.length) return texts.join(', ')
  }
  if (data && typeof data.error === 'string' && data.error) return data.error
  if (err && err.response && err.response.status === 401) {
    return 'ไม่มีสิทธิ์เข้าถึงข้อมูลผู้ป่วย'
  }
  return (err && err.message) || 'บันทึกไม่สำเร็จ'
}

export function tableOptionsKey (options) {
  const opts = options || {}
  const page = opts.page || 1
  const itemsPerPage = opts.itemsPerPage || 10
  const sortBy = (opts.sortBy && opts.sortBy[0]) || ''
  const sortDesc = (opts.sortDesc && opts.sortDesc[0]) ? '1' : '0'
  return `${page}|${itemsPerPage}|${sortBy}|${sortDesc}`
}
